/* eslint-disable no-unused-vars */
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import {
  Quotation,
  QuotationCategory,
  QuotationParams,
  QuotationProduct
} from 'src/types';
import { isEmpty } from 'lodash';

const {
  selectors: quotationSelectors,
  actions: quotationActions
} = slices.quotationV2;

const useQuotationProduct = () => {
  const dispatch = useAppDispatch();

  const quotationData = useAppSelector(quotationSelectors?.selectQuotationData);
  const { is_template_loading, templateDetails } = useAppSelector(
    quotationSelectors?.selectTemplate
  );
  const { is_loading: is_loading_products, productOptions } = useAppSelector(
    quotationSelectors?.selectProductOptions
  );
  const quotationParams = useAppSelector(
    quotationSelectors?.selectQuotationParams
  );

  const setQuotationParams = (params: QuotationParams) => {
    dispatch(quotationActions?.setQuotationParams(params));
  };

  const onChangeQuotationCategoryRow = (
    index: number,
    newCategory: QuotationCategory,
    currentCategory: QuotationCategory,
    quotationData: Quotation[]
  ) => {
    const newQuotationData = quotationData.map((quotation, currentIndex) => {
      if (
        quotation?.product?.category_id === currentCategory?.category_id &&
        index === currentIndex
      ) {
        return {
          ...quotation,
          quantity: 1,
          product: {
            category_id: newCategory.category_id,
            category_name: newCategory.category_name
          }
        };
      }
      return quotation;
    });

    dispatch(quotationActions.setChangeCategoryTypeRowAction(newQuotationData));
  };

  const onChangeCustomCategoryRow = (
    index: number,
    newCategoryName: string,
    quotationData: Quotation[]
  ) => {
    const newQuotationData = quotationData.map((quotation, currentIndex) => {
      if (currentIndex !== index) return quotation;

      return {
        ...quotation,
        quantity: 1,
        product: {
          ...quotation.product,
          category_id: 0,
          category_name: newCategoryName
        },
        custom: true
      };
    });

    dispatch(quotationActions.setChangeCategoryTypeRowAction(newQuotationData));
  };

  const onChangeProduct = (
    index: number,
    category: QuotationCategory,
    selectedProduct: QuotationProduct,
    quotationData: Quotation[]
  ) => {
    const newQuotationData = quotationData.map((quotation, currentIndex) => {
      if (
        quotation?.product?.category_id === category?.category_id &&
        currentIndex === index
      ) {
        return {
          ...quotation,
          quantity: 1,
          product: isEmpty(selectedProduct)
            ? {
                category_id: category.category_id,
                category_name: category.category_name,
                product_name: '',
                retail_price: 0
              }
            : selectedProduct
        };
      }
      return quotation;
    });

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const onChangeQuantity = (
    index: number,
    quotationData: Quotation[],
    category: QuotationCategory,
    newQty: number
  ) => {
    const newQuotationData = quotationData.map((quotation, currentIndex) => {
      if (
        quotation?.product?.category_id === category?.category_id &&
        currentIndex === index
      ) {
        return {
          ...quotation,
          quantity: newQty
        };
      }
      return quotation;
    });

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const onChangeCustomPrice = (
    index: number,
    quotationData: Quotation[],
    newPrice: number
  ) => {
    const newQuotationData = quotationData.map((quotation, currentIndex) => {
      if (currentIndex === index && quotation.custom) {
        return {
          ...quotation,
          product: {
            ...quotation.product,
            retail_price: newPrice
          }
        };
      }
      return quotation;
    });

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const applySrpToQuotation = (
    applySrp: boolean,
    quotationData: Quotation[]
  ) => {
    if (!quotationData) return;

    const updatedQuotationData = quotationData.map((quotation) => {
      const product = quotation.product;
      if (!product) return quotation;

      //I HATE TYPESCRIPT!
      const basePrice = product.retail_price || 0;

      return {
        ...quotation,
        product: {
          ...product,
          retail_price: applySrp
            ? +(basePrice * 1.12).toFixed(2)
            : +(basePrice / 1.12).toFixed(2)
        }
      };
    });

    dispatch(
      quotationActions.setChangeQuotationProductAction(updatedQuotationData)
    );
  };

  const onAddStockRow = (quotationData: Quotation[]) => {
    const newQuotationData = [...quotationData, { quantity: 1, product: {} }];
    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const onAddCustomRow = (quotationData: Quotation[], voucherRow?: boolean) => {
    const newQuotationData = [
      ...quotationData,
      ...(!voucherRow ? [{ quantity: 1, product: {}, custom: true }] : [])
    ];
    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const removeEmptyRows = (quotationData: Quotation[]) => {
    const newQuotationData = quotationData.filter(
      (quotation) =>
        quotation?.product?.product_name !== '' &&
        quotation?.product?.product_name !== undefined &&
        quotation?.product?.product_name !== null
    );

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const onRemoveQuotation = (index: number, quotationData: Quotation[]) => {
    const newQuotationData = quotationData.filter(
      (_, currentIndex) =>
        currentIndex !== index && _.product?.category_name !== 'VOUCHER'
    );

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const onChangeRemoveVoucherRow = (quotationData: Quotation[]) => {
    const newQuotationData = quotationData.filter(
      (item) => item?.product?.category_name !== 'VOUCHER'
    );

    dispatch(
      quotationActions.setChangeQuotationProductAction(newQuotationData)
    );
  };

  const resetQuotation = () => {
    dispatch(quotationActions?.resetQuotationAction());
  };

  const resetProductOptions = () => {
    dispatch(quotationActions?.resetProductOptionsAction());
  };

  return {
    is_loading_products,
    is_template_loading,
    productOptions,
    quotationData,
    quotationParams,
    templateDetails,

    onChangeQuotationCategoryRow,
    onChangeCustomCategoryRow,
    onChangeProduct,
    setQuotationParams,
    onChangeQuantity,
    onChangeCustomPrice,
    onAddStockRow,
    onAddCustomRow,

    removeEmptyRows,
    onRemoveQuotation,
    onChangeRemoveVoucherRow,
    resetQuotation,
    resetProductOptions,

    applySrpToQuotation
  };
};

export default useQuotationProduct;
